import { ClientsPage } from '@components/dashboard/clients';
import { DashboardHome } from '@components/dashboard/DashboardHome';
import { DashboardLayout } from '@components/dashboard/DashboardLayout';
import { DepartmentsPage } from '@components/dashboard/departments';
import { MenuItemsPage } from '@components/dashboard/menu';
import {
  CreditBillsPage,
  CreditorsReportPage,
  DailySalesReportPage,
  DepartmentSalesReportPage,
  DiscountsReportPage,
  ItemsReportPage,
  KotTrackingReportPage,
  OrdersReportPage,
  PaymentMethodsReportPage,
  SettlementsReportPage,
} from '@components/dashboard/reports';
import { SectionsAndTablesPage } from '@components/dashboard/sections';
import { useAppSelector } from '@hooks/useRedux';
import FirstTimeSetupPage from '@pages/FirstTimeSetupPage';
import { AccessControlCode } from '@shared/constants';
import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { OutletsPage } from '../pages/outlets';
import { InventoryReportsPage, SettingsPage } from '../pages/placeholders';
import {
  selectNeedsFirstTimeOnboarding,
  selectPreferredDashboardRoute,
} from '../store/auth/authSelectors';
import { BILLING_PAGE_PATH } from '../util/renderConstants';
import { AccessControlledDashboardPage } from './featureRouteHelpers';

const DashboardIndexRoute: React.FC = () => {
  const preferredRoute = useAppSelector(selectPreferredDashboardRoute);
  const needsFirstTimeOnboarding = useAppSelector(
    selectNeedsFirstTimeOnboarding,
  );

  if (needsFirstTimeOnboarding) {
    return <Navigate to="/dashboard/getting-started" replace />;
  }

  if (!preferredRoute) {
    return <Navigate to="/sales" replace />;
  }

  if (preferredRoute !== '/dashboard') {
    return <Navigate to={preferredRoute} replace />;
  }

  return <DashboardHome />;
};

export const createDashboardRoutes = () => (
  <Route path="/dashboard" element={<DashboardLayout />}>
    <Route index element={<DashboardIndexRoute />} />
    <Route path="getting-started" element={<FirstTimeSetupPage />} />
    <Route
      path="departments"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_PAGE_ACCESS}
          title="Departments access restricted"
          description="Your account does not currently have permission to access the dashboard."
        >
          <DepartmentsPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="menu"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_PAGE_ACCESS}
          title="Menu access restricted"
          description="Your account does not currently have permission to access the dashboard."
        >
          <MenuItemsPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="sections-tables"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_PAGE_ACCESS}
          title="Sections and tables access restricted"
          description="Your account does not currently have permission to access the dashboard."
        >
          <SectionsAndTablesPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="outlets"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_PAGE_ACCESS}
          title="Outlet access restricted"
          description="Your account does not currently have permission to access the dashboard."
        >
          <OutletsPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="clients"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_CLIENTS_VIEW}
          title="Client access restricted"
          description="Your account does not currently have permission to view company clients."
        >
          <ClientsPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="reports"
      element={<Navigate to="/dashboard/reports/orders" replace />}
    />
    <Route
      path="reports/sales"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW}
          title="Sales reports access restricted"
          description="Your account does not currently have permission to view sales reports."
        >
          <DailySalesReportPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="reports/orders"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW}
          title="Sales reports access restricted"
          description="Your account does not currently have permission to view sales reports."
        >
          <OrdersReportPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="reports/settlements"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW}
          title="Sales reports access restricted"
          description="Your account does not currently have permission to view sales reports."
        >
          <SettlementsReportPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="reports/daily-sales"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW}
          title="Sales reports access restricted"
          description="Your account does not currently have permission to view sales reports."
        >
          <DailySalesReportPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="reports/payment-methods"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW}
          title="Sales reports access restricted"
          description="Your account does not currently have permission to view sales reports."
        >
          <PaymentMethodsReportPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="reports/departments"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW}
          title="Sales reports access restricted"
          description="Your account does not currently have permission to view sales reports."
        >
          <DepartmentSalesReportPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="reports/items"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW}
          title="Sales reports access restricted"
          description="Your account does not currently have permission to view sales reports."
        >
          <ItemsReportPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="reports/discounts"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW}
          title="Sales reports access restricted"
          description="Your account does not currently have permission to view sales reports."
        >
          <DiscountsReportPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="reports/kot-tracking"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW}
          title="Sales reports access restricted"
          description="Your account does not currently have permission to view sales reports."
        >
          <KotTrackingReportPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="reports/creditors"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW}
          title="Sales reports access restricted"
          description="Your account does not currently have permission to view sales reports."
        >
          <CreditorsReportPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="reports/credit-bills"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW}
          title="Sales reports access restricted"
          description="Your account does not currently have permission to view sales reports."
        >
          <CreditBillsPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="reports/inventory"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW}
          title="Sales reports access restricted"
          description="Your account does not currently have permission to view sales reports."
        >
          <InventoryReportsPage />
        </AccessControlledDashboardPage>
      }
    />
    <Route
      path="settings"
      element={
        <AccessControlledDashboardPage
          control={AccessControlCode.DASHBOARD_SETTINGS_ACCESS}
          title="Settings access restricted"
          description="Your account does not currently have permission to access settings."
        >
          <SettingsPage />
        </AccessControlledDashboardPage>
      }
    />
    {/* Billing moved to Settings — keep old links and bookmarks working. */}
    <Route
      path="billing"
      element={<Navigate to={BILLING_PAGE_PATH} replace />}
    />
  </Route>
);

export const dashboardRoutes = [
  { path: '', label: 'Dashboard', component: DashboardHome },
  { path: 'departments', label: 'Departments', component: DepartmentsPage },
  { path: 'menu', label: 'Menu Items', component: MenuItemsPage },
  {
    path: 'sections-tables',
    label: 'Sections & Tables',
    component: SectionsAndTablesPage,
  },
  { path: 'outlets', label: 'Outlets', component: OutletsPage },
  { path: 'clients', label: 'Clients', component: ClientsPage },
  {
    path: 'reports/orders',
    label: 'Orders Report',
    component: OrdersReportPage,
  },
  {
    path: 'reports/settlements',
    label: 'Settlements Report',
    component: SettlementsReportPage,
  },
  {
    path: 'reports/daily-sales',
    label: 'Daily Sales Report',
    component: DailySalesReportPage,
  },
  {
    path: 'reports/payment-methods',
    label: 'Sales Summary Report',
    component: PaymentMethodsReportPage,
  },
  {
    path: 'reports/departments',
    label: 'Department Sales Report',
    component: DepartmentSalesReportPage,
  },
  {
    path: 'reports/items',
    label: 'Items Report',
    component: ItemsReportPage,
  },
  {
    path: 'reports/discounts',
    label: 'Discounts Report',
    component: DiscountsReportPage,
  },
  {
    path: 'reports/creditors',
    label: 'Creditors & NC Report',
    component: CreditorsReportPage,
  },
  {
    path: 'reports/kot-tracking',
    label: 'KOT Tracking',
    component: KotTrackingReportPage,
  },
  {
    path: 'reports/credit-bills',
    label: 'Credit Bills',
    component: CreditBillsPage,
  },
  {
    path: 'reports/inventory',
    label: 'Inventory Reports',
    component: InventoryReportsPage,
  },
  { path: 'settings', label: 'Settings', component: SettingsPage },
] as const;
